import React, { useEffect, useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { INPUT_EVENT } from 'constants/events';
import {
  DOLLAR_SIGN,
  ESCAPED_DOLLAR,
  MATH_FIELD,
  USER_TYPE
} from 'constants/index';

import { traverseNodes } from 'utils/latexRenderer';

import styles from './styles.module.scss';

const BlockEmbed = Quill.import('blots/block/embed');

class MathBlot extends BlockEmbed {
  static blotName = 'math';
  static tagName = 'span';
  static className = 'math-field-wrapper';

  static create(value) {
    const node = super.create();
    const mathField = document.createElement(MATH_FIELD);
    mathField.value = value;
    mathField.className = styles.inlineMath;
    node.appendChild(mathField);

    // Live update math content
    mathField.addEventListener(INPUT_EVENT, () => {
      node.dataset.value = mathField.value;
    });

    node.dataset.value = value;
    return node;
  }

  static value(node) {
    return node.dataset.value || '';
  }
}

Quill.register(MathBlot);

const icons = Quill.import('ui/icons');
icons['insertMath'] = '∑';

// ---------------------------------------

const EditorField = ({ initialValue = '', onChange, readOnly = false }) => {
  const quillRef = useRef(null);
  const editorRef = useRef(null);
  const mounted = useRef(false);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor || mounted.current) return; // Added guard here

    editorRef.current = editor;

    // Normalize and PREVENT triple newlines
    const normalizedValue = initialValue
      .replace(/\\\\/g, '\\')
      .replace(/\\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n');

    const protectedValue = normalizedValue.replace(/\\\$/g, ESCAPED_DOLLAR);
    const lines = protectedValue.split(/(\$[^$]+\$)/g);

    editor.setContents([]);

    lines.forEach((line, index) => {
      // Logic for trailing punctuation/empty lines
      if (
        (line === '' || line === ',' || line === '.') &&
        index === lines.length - 1
      ) {
        // Only insert if the editor isn't already ending in a newline
        const lastChar = editor.getText(editor.getLength() - 2, 1);
        if (lastChar !== '\n') {
          editor.insertText(editor.getLength(), '\n', 'silent');
        }
        return;
      }

      if (line.startsWith(DOLLAR_SIGN) && line.endsWith(DOLLAR_SIGN)) {
        const latex = line.slice(1, -1).replace(/\\\\/g, '\\');
        // If editor is "empty" (length 1 is just the trailing \n)
        if (editor.getLength() <= 1) {
          editor.insertEmbed(0, 'math', latex);
        } else {
          editor.insertEmbed(editor.getLength() - 1, 'math', latex);
        }
      } else {
        const restored = line.replace(new RegExp(ESCAPED_DOLLAR, 'g'), '$');

        if (!restored) return;

        const pos = editor.getLength() - 1;
        editor.insertText(pos, restored);
      }
    });

    // Instead of setting the cursor at the start, set it at the end AFTER the loop
    setTimeout(() => {
      editor.setSelection(editor.getLength(), 0);
    }, 0);

    mounted.current = true;
  }, [initialValue]);
  const handleChange = ({ source }) => {
    if (source !== USER_TYPE) return;

    const editor = editorRef.current;
    if (!editor || !editor.root) return;

    const updated = traverseNodes({ node: editor.root });
    onChange?.(updated);
  };

  const modules = useMemo(() => {
    if (readOnly) return false;

    return {
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['clean'],
          [{ formula: [] }],
          ['insertMath'] // custom button key
        ],
        handlers: {
          insertMath: () => {
            const editor = quillRef.current?.getEditor();
            if (!editor) return;

            const length = editor.getLength();
            editor.insertEmbed(length, 'math', '');
            editor.insertText(editor.getLength(), ' ');

            setTimeout(() => {
              editor.setSelection(editor.getLength(), 0);
            }, 0);
          }
        }
      }
    };
  }, [readOnly]);

  return (
    <ReactQuill
      ref={quillRef}
      readOnly={readOnly}
      theme={readOnly ? 'bubble' : 'snow'}
      onChange={(content, delta, source) => {
        handleChange({ content, delta, source });
      }}
      modules={modules}
      placeholder="Type text with markup and math..."
    />
  );
};

export default EditorField;
